import { Link } from 'react-router-dom'
import { Shield, TrendingUp, Heart, Laptop, Users } from 'lucide-react'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DEMO_CONFIGS } from '@/lib/demo/constants'

const DEMO_CARDS = [
  {
    demoId: 'insurance-renewal' as const,
    industry: 'Insurance',
    Icon: Shield,
    badge: null,
  },
  {
    demoId: 'financial-services-fees' as const,
    industry: 'Financial Services',
    Icon: TrendingUp,
    badge: null,
  },
  {
    demoId: 'healthcare-reminder' as const,
    industry: 'Healthcare',
    Icon: Heart,
    badge: null,
  },
  {
    demoId: 'saas-churn' as const,
    industry: 'SaaS',
    Icon: Laptop,
    badge: null,
  },
  {
    demoId: 'preference-center-research' as const,
    industry: 'Twin Panel',
    Icon: Users,
    badge: 'New',
  },
]

export function DemoLandingPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-16 space-y-12">
        <div className="text-center space-y-4">
          <Badge variant="demo">Interactive demo · No signup required</Badge>
          <h1 className="text-4xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>
            See Echosome in action
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Pick a demo below. Industry demos walk through a complete pre-read workflow with real findings and compliance signals. The Twin Panel demo shows backtest-validated 1:1 modeling from a 1,200-respondent bank preference-center study.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {DEMO_CARDS.map(({ demoId, industry, Icon, badge }) => {
            const config = DEMO_CONFIGS[demoId]
            const headlineFirst = config.approvedHeadline.split('—')[0].trim()
            const isTwinPanel = demoId === 'preference-center-research'
            return (
              <Card
                key={demoId}
                data-testid="demo-card"
                data-industry={demoId}
                className="space-y-3 p-5"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className={`flex items-center gap-2 ${isTwinPanel ? 'text-echo-indigo' : 'text-human-teal'}`}>
                    <Icon size={18} strokeWidth={1.75} />
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {industry}
                    </span>
                  </div>
                  {badge && (
                    <Badge variant="demo">{badge}</Badge>
                  )}
                </div>
                <p className="text-base font-medium text-deep-ink">{config.title}</p>
                <p className="text-sm text-slate-600">{headlineFirst}</p>
                <Link
                  to={`/app/demo?industry=${demoId}`}
                  className={`inline-block text-sm font-medium hover:underline ${isTwinPanel ? 'text-echo-indigo' : 'text-human-teal'}`}
                >
                  Explore demo &rarr;
                </Link>
              </Card>
            )
          })}
        </div>

        <div className="text-center">
          <Link to="/signup">
            <Button size="lg" variant="primary">Sign up free to use your own data</Button>
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  )
}
